import './Settings.css';

import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';

import {apiService} from '../../apiService';
import {useAuth} from '../../contexts/AuthContext';
import {userInfoQueryKey} from '../../hooks/useUserInfo';

// Validation regex patterns
const VALIDATION_PATTERNS = {
  PHONE: /^\d{10}$/,
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  NAME: /^[a-zA-Z\s-']+$/,
};

const Settings = () => {
  const {currentUser} = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAllSites, setShowAllSites] = useState(false);
  const [organizationData, setOrganizationData] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [originalOrganizations, setOriginalOrganizations] = useState([]);
  const [newOrgCode, setNewOrgCode] = useState('');
  const [addingOrg, setAddingOrg] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [notificationsByOrg, setNotificationsByOrg] = useState({});
  const [originalNotificationsByOrg, setOriginalNotificationsByOrg] = useState(
    {},
  );
  const [orgSaveErrors, setOrgSaveErrors] = useState({});

  // State for user information
  const [userInfo, setUserInfo] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    comp_code: '', // Add comp_code to state
    company_name: '', // Add company_name to state
    phone_notification: false,
    email_notification: false,
    text_notification: false,
    alert_flagged: true,
    push_only_impaired: true,
    email_only_impaired: true,
  });

  const [originalUserInfo, setOriginalUserInfo] = useState({});

  const [validationErrors, setValidationErrors] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    notification_methods: '',
  });

  const invalidateUserInfo = () => {
    queryClient.invalidateQueries({
      queryKey: userInfoQueryKey(currentUser?.uid),
    });
  };

  // Check if user is admin
  const checkUserAdminStatus = async () => {
    try {
      const data = await apiService.get(
        `/api/check-user-admin-status?uid=${currentUser.uid}`,
      );
      return {
        isAdmin: data.role === 'admin',
      };
    } catch (err) {
      console.error('Error checking admin status:', err);
      return {isAdmin: false};
    }
  };

  // Fetch organization data
  const fetchOrganizationData = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoadingOrgData(true);
      const url = `/api/get-manager-employees?uid=${currentUser.uid}`;
      const data = await apiService.get(url);

      if (data && data) {
        const employees = data;

        // Process data similar to Team.jsx organizeDataForAdmin function
        const locations = {};

        employees.forEach((employee) => {
          const companyName = employee.company_name || 'Unknown Organization';

          // Initialize location if it doesn't exist
          if (!locations[companyName]) {
            locations[companyName] = {
              subOrgId: employee.company_code || 'N/A',
              locationName: companyName,
              managers: [],
            };
          }

          // Add managers to this location
          if (
            employee.user_type === 'manager' &&
            employee.first_name &&
            employee.last_name
          ) {
            const managerName =
              `${employee.first_name} ${employee.last_name}`.trim();
            if (!locations[companyName].managers.includes(managerName)) {
              locations[companyName].managers.push(managerName);
            }
          }

          // Handle location_placeholder type which might just establish the location
          // even if no specific employees are listed
        });

        // Convert to array format
        const orgData = Object.values(locations);
        setOrganizationData(orgData);
      }
    } catch (err) {
      console.error('Error fetching organization data:', err);
    } finally {
      setLoadingOrgData(false);
    }
  };

  // Handle show all sites checkbox
  const handleShowAllSitesChange = async () => {
    const newValue = !showAllSites;
    setShowAllSites(newValue);

    if (newValue && organizationData.length === 0) {
      await fetchOrganizationData();
    }
  };

  // Fetch user data when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;

      try {
        setLoading(true);
        setError('');

        // Check admin status
        const adminInfo = await checkUserAdminStatus();
        setIsAdmin(adminInfo.isAdmin);

        const data = await apiService.get(
          `/api/get-manager-info?uid=${currentUser.uid}`,
        );

        if (data) {
          const userData = data;

          const formattedUserData = {
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            phone_number: userData.phone_number || '',
            email: userData.email || '',
            comp_code: userData.comp_code || '',
            company_name: userData.company_name || '',
            phone_notification: Boolean(userData.app_notif),
            email_notification: Boolean(userData.email_notif),
            text_notification: Boolean(userData.text_notif),
            alert_flagged: Boolean(userData.alert_when_flagged),
            push_only_impaired: Boolean(userData.push_only_impaired || false),
            email_only_impaired: Boolean(userData.email_only_impaired || false),
          };
          setUserInfo(formattedUserData);
          setOriginalUserInfo(formattedUserData);
          if (userData.organizations) {
            setOrganizations(userData.organizations);
            setOriginalOrganizations(userData.organizations);
            const initialNotifs = {};
            userData.organizations.forEach((o) => {
              initialNotifs[o.org_id] = {
                phone_notification: Boolean(o.app_notif),
                email_notification: Boolean(o.email_notif),
                push_only_impaired: Boolean(o.push_only_impaired),
                email_only_impaired: Boolean(o.email_only_impaired),
                alert_flagged: Boolean(o.alert_when_flagged),
              };
            });
            setNotificationsByOrg(initialNotifs);
            setOriginalNotificationsByOrg(initialNotifs);
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Validate a single field
  const validateField = (fieldName, value) => {
    if (!editMode) return '';

    let errorMessage = '';

    // Check for empty required fields
    if (
      (fieldName === 'first_name' ||
        fieldName === 'last_name' ||
        fieldName === 'phone_number') &&
      !value.trim()
    ) {
      return `${fieldName.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
    }

    switch (fieldName) {
      case 'first_name':
      case 'last_name':
        if (value && !VALIDATION_PATTERNS.NAME.test(value)) {
          errorMessage =
            'Name should only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      case 'phone_number':
        if (value && !VALIDATION_PATTERNS.PHONE.test(value)) {
          errorMessage = 'Phone number must be exactly 10 digits';
        }
        break;
      case 'email':
        if (value && !VALIDATION_PATTERNS.EMAIL.test(value)) {
          errorMessage = 'Please enter a valid email address';
        }
        break;
      case 'notification_methods':
        if (!value) {
          errorMessage = 'At least one notification method must be selected';
        }
        break;
      default:
        break;
    }

    return errorMessage;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    if (!editMode) return;

    const {id, value} = e.target;

    // Update the user info
    setUserInfo({...userInfo, [id]: value});

    // Validate the field
    const errorMessage = validateField(id, value);
    setValidationErrors({
      ...validationErrors,
      [id]: errorMessage,
    });
  };

  // Handle phone number input specifically
  const handlePhoneChange = (e) => {
    if (!editMode) return;

    // Only allow numeric input
    const value = e.target.value.replace(/\D/g, '');

    setUserInfo({...userInfo, phone_number: value});

    // Validate
    const errorMessage = validateField('phone_number', value);
    setValidationErrors({
      ...validationErrors,
      phone_number: errorMessage,
    });
  };

  const handleAlertToggle = (orgId, key) => {
    if (!editMode) return;
    setNotificationsByOrg((prev) => {
      const cur = prev[orgId] || {};
      const next = {...cur};
      if (key === 'phone_notification') {
        next.phone_notification = !cur.phone_notification;
        if (!next.phone_notification) next.push_only_impaired = false;
      } else if (key === 'email_notification') {
        next.email_notification = !cur.email_notification;
        if (!next.email_notification) next.email_only_impaired = false;
      } else if (key === 'push_only_impaired') {
        if (!cur.phone_notification) return prev;
        next.push_only_impaired = !cur.push_only_impaired;
      } else if (key === 'email_only_impaired') {
        if (!cur.email_notification) return prev;
        next.email_only_impaired = !cur.email_only_impaired;
      }
      return {...prev, [orgId]: next};
    });
  };

  const buildNotifPayload = (orgId, settings) => ({
    uid: currentUser.uid,
    org_id: orgId,
    first_name: userInfo.first_name,
    last_name: userInfo.last_name,
    phone_number: userInfo.phone_number,
    phone_notification: settings.phone_notification ? 1 : 0,
    email_notification: settings.email_notification ? 1 : 0,
    push_only_impaired: settings.push_only_impaired ? 1 : 0,
    email_only_impaired: settings.email_only_impaired ? 1 : 0,
    alert_flagged: settings.alert_flagged ? 1 : 0,
  });

  // Handle checkbox changes
  const handleCheckboxChange = (setting) => {
    if (!editMode) return; // Skip if not in edit mode

    let updatedUserInfo = {...userInfo};

    if (setting === 'alert_flagged') {
      updatedUserInfo.alert_flagged = !userInfo.alert_flagged;
    } else if (setting === 'phone_notification') {
      updatedUserInfo.phone_notification = !userInfo.phone_notification;

      // If turning off phone notification, also turn off its subsetting
      if (!updatedUserInfo.phone_notification) {
        updatedUserInfo.push_only_impaired = false;
      }
    } else if (setting === 'email_notification') {
      updatedUserInfo.email_notification = !userInfo.email_notification;

      // If turning off email notification, also turn off its subsetting
      if (!updatedUserInfo.email_notification) {
        updatedUserInfo.email_only_impaired = false;
      }
    } else if (setting === 'push_only_impaired') {
      // Only allow toggling if parent setting is enabled
      if (userInfo.phone_notification) {
        updatedUserInfo.push_only_impaired = !userInfo.push_only_impaired;
      }
    } else if (setting === 'email_only_impaired') {
      // Only allow toggling if parent setting is enabled
      if (userInfo.email_notification) {
        updatedUserInfo.email_only_impaired = !userInfo.email_only_impaired;
      }
    } else {
      updatedUserInfo[setting] = !userInfo[setting];
    }

    setUserInfo(updatedUserInfo);
  };

  // Validate all fields
  const validateAllFields = () => {
    if (!editMode) return true;

    const errors = {
      first_name: validateField('first_name', userInfo.first_name),
      last_name: validateField('last_name', userInfo.last_name),
      phone_number: validateField('phone_number', userInfo.phone_number),
      email: validateField('email', userInfo.email),
    };

    setValidationErrors(errors);

    return !Object.values(errors).some((error) => error !== '');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editMode) {
      // If not in edit mode, enable edit mode and return
      setEditMode(true);
      return;
    }

    if (!currentUser?.uid) {
      setError('User ID not found. Please log in again.');
      return;
    }

    // Validate all fields before submission
    if (!validateAllFields()) {
      setError('Please correct the validation errors before saving.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      setOrgSaveErrors({});

      const originalIds = new Set(originalOrganizations.map((o) => o.org_id));
      const currentIds = new Set(organizations.map((o) => o.org_id));
      const orgsToAdd = organizations.filter((o) => !originalIds.has(o.org_id));
      const orgsToRemove = originalOrganizations.filter(
        (o) => !currentIds.has(o.org_id),
      );

      const failures = {};

      for (const org of orgsToAdd) {
        try {
          const resp = await apiService.post('/api/add-manager-org/', {
            uid: currentUser.uid,
            comp_code: org.company_code,
          });
          if (resp?.error) {
            failures[org.org_id] = resp.error;
          }
        } catch (err) {
          failures[org.org_id] =
            err.response?.data?.error || 'Failed to add organization.';
        }
      }

      for (const org of orgsToRemove) {
        try {
          const resp = await apiService.post('/api/remove-manager-org/', {
            uid: currentUser.uid,
            org_id: org.org_id,
          });
          if (resp?.error) {
            failures[org.org_id] = resp.error;
          }
        } catch (err) {
          failures[org.org_id] =
            err.response?.data?.error || 'Failed to remove organization.';
        }
      }

      const notifTargets = organizations.filter(
        (o) =>
          !failures[o.org_id] &&
          !orgsToRemove.some((r) => r.org_id === o.org_id),
      );
      const results = await Promise.allSettled(
        notifTargets.map((org) => {
          const settings = notificationsByOrg[org.org_id] || {};
          return apiService
            .put(
              '/api/update-manager-info/',
              buildNotifPayload(org.org_id, settings),
            )
            .then((data) => ({org, ok: !!data}));
        }),
      );

      results.forEach((r, i) => {
        const org = notifTargets[i];
        if (r.status === 'rejected' || !r.value?.ok) {
          failures[org.org_id] = 'Failed to save for this organization.';
        }
      });

      if (orgsToAdd.length > 0 || orgsToRemove.length > 0) {
        invalidateUserInfo();
      }

      if (Object.keys(failures).length === 0) {
        setSuccessMessage('Settings updated successfully!');
        setEditMode(false);
        setOriginalUserInfo({...userInfo});
        setOriginalNotificationsByOrg({...notificationsByOrg});
        setOriginalOrganizations(organizations);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setOrgSaveErrors(failures);
        setError('Some changes failed to save. See details below.');
      }
    } catch (err) {
      console.error('Error updating user data:', err);
      setError('Failed to update settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const retryOrgSave = async (orgId) => {
    const org = organizations.find((o) => o.org_id === orgId);
    if (!org) return;
    const settings = notificationsByOrg[orgId] || {};
    setSaving(true);
    try {
      const data = await apiService.put(
        '/api/update-manager-info/',
        buildNotifPayload(orgId, settings),
      );
      if (data) {
        setOrgSaveErrors((prev) => {
          const next = {...prev};
          delete next[orgId];
          if (Object.keys(next).length === 0) {
            setError('');
            setSuccessMessage('Settings updated successfully!');
            setEditMode(false);
            setOriginalUserInfo({...userInfo});
            setOriginalNotificationsByOrg({...notificationsByOrg});
            setTimeout(() => setSuccessMessage(''), 3000);
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setUserInfo({...originalUserInfo});
    setNotificationsByOrg({...originalNotificationsByOrg});
    setOrganizations(originalOrganizations);
    setNewOrgCode('');
    setOrgError('');
    setOrgSaveErrors({});
    setValidationErrors({
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
      notification_methods: '',
    });
    setError('');
    setSuccessMessage('');
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone || phone.length !== 10) return phone;
    return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
  };

  const handleAddOrg = async () => {
    if (!newOrgCode.trim()) return;

    setAddingOrg(true);
    setOrgError('');

    try {
      const verifyResp = await apiService.post('/api/verify-org-code/', {
        company_code: parseInt(newOrgCode),
        admin: 0,
        uid: currentUser?.uid,
      });

      if (!verifyResp.valid) {
        setOrgError('Invalid organization code.');
        return;
      }

      if (organizations.some((o) => o.org_id === verifyResp.org_id)) {
        setOrgError('Organization already added.');
        return;
      }

      setOrganizations((prev) => [
        ...prev,
        {
          org_id: verifyResp.org_id,
          company_name: verifyResp.company_name,
          company_code: verifyResp.company_code,
        },
      ]);
      setNotificationsByOrg((prev) => ({
        ...prev,
        [verifyResp.org_id]: prev[verifyResp.org_id] || {
          phone_notification: false,
          email_notification: false,
          push_only_impaired: false,
          email_only_impaired: false,
          alert_flagged: true,
        },
      }));
      setNewOrgCode('');
    } catch (err) {
      setOrgError(
        err.response?.data?.error || 'Failed to verify organization.',
      );
    } finally {
      setAddingOrg(false);
    }
  };

  const handleRemoveOrg = (org) => {
    if (organizations.length <= 1) return;
    setOrgError('');
    setOrganizations((prev) => prev.filter((o) => o.org_id !== org.org_id));
  };

  if (loading) {
    return <div className='settings-loading'>Loading user information...</div>;
  }

  return (
    <div className='settings-content'>
      {error && <div className='settings-error'>{error}</div>}
      {successMessage && (
        <div className='settings-success'>{successMessage}</div>
      )}

      <div className='settings-header'>
        <h2>User Settings</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <section className='settings-section'>
          <h2>Personal Information</h2>

          <div className='form-group'>
            <label htmlFor='first_name'>First Name *</label>
            {editMode ? (
              <input
                type='text'
                id='first_name'
                className={`form-control ${validationErrors.first_name ? 'invalid' : ''}`}
                placeholder='Enter your first name'
                value={userInfo.first_name}
                onChange={handleInputChange}
                required
              />
            ) : (
              <div className='form-control readonly'>{userInfo.first_name}</div>
            )}
            {validationErrors.first_name && (
              <div className='validation-error'>
                {validationErrors.first_name}
              </div>
            )}
          </div>

          <div className='form-group'>
            <label htmlFor='last_name'>Last Name *</label>
            {editMode ? (
              <input
                type='text'
                id='last_name'
                className={`form-control ${validationErrors.last_name ? 'invalid' : ''}`}
                placeholder='Enter your last name'
                value={userInfo.last_name}
                onChange={handleInputChange}
                required
              />
            ) : (
              <div className='form-control readonly'>{userInfo.last_name}</div>
            )}
            {validationErrors.last_name && (
              <div className='validation-error'>
                {validationErrors.last_name}
              </div>
            )}
          </div>

          <div className='form-group'>
            <label htmlFor='phone_number'>Phone Number (10 digits) *</label>
            {editMode ? (
              <input
                type='tel'
                id='phone_number'
                className={`form-control ${validationErrors.phone_number ? 'invalid' : ''}`}
                placeholder='Enter your phone number (digits only)'
                value={userInfo.phone_number}
                onChange={handlePhoneChange}
                maxLength={10}
                required
              />
            ) : (
              <div className='form-control readonly'>
                {formatPhoneForDisplay(userInfo.phone_number)}
              </div>
            )}
            {validationErrors.phone_number && (
              <div className='validation-error'>
                {validationErrors.phone_number}
              </div>
            )}
          </div>

          <div className='form-group'>
            <label htmlFor='email'>Email</label>
            <div className='form-control readonly'>{userInfo.email}</div>
            {validationErrors.email && (
              <div className='validation-error'>{validationErrors.email}</div>
            )}
          </div>
          <div className='form-note'>* Required fields</div>
        </section>

        <section className='settings-section'>
          <h2>Organization and Impairment Screening</h2>

          {isAdmin ? (
            <>
              <div className='form-group'>
                <label htmlFor='comp_code'>
                  Master Organization Code
                  <span className='org-id-disclaimer'>
                    *Do not share this code with non-admins
                  </span>
                </label>
                <div className='form-control readonly'>
                  {userInfo.comp_code || 'Not Available'}
                </div>
              </div>

              <div className='form-group'>
                <div className='checkbox-item'>
                  <input
                    type='checkbox'
                    id='show_all_sites'
                    checked={showAllSites}
                    onChange={handleShowAllSitesChange}
                  />
                  <label htmlFor='show_all_sites'>
                    Show All Sites and Respective Sub Organization Codes
                  </label>
                </div>
              </div>

              {showAllSites && (
                <div className='organization-data'>
                  {loadingOrgData ? (
                    <div className='loading-org-data'>
                      Loading organization data...
                    </div>
                  ) : (
                    <div className='organization-table'>
                      <div className='table-header'>
                        <div className='table-column'>
                          Sub Organization Code
                        </div>
                        <div className='table-column'>Location Name</div>
                        <div className='table-column'>Manager(s)</div>
                      </div>
                      {organizationData.length > 0 ? (
                        organizationData
                          .filter((org) => org.subOrgId !== userInfo.comp_code)
                          .map((org, index) => (
                            <div key={index} className='table-row'>
                              <div className='table-cell'>{org.subOrgId}</div>
                              <div className='table-cell'>
                                {org.locationName}
                              </div>
                              <div className='table-cell'>
                                {org.managers.length > 0
                                  ? org.managers.join(', ')
                                  : 'No managers assigned'}
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className='no-organization-data'>
                          No organization data available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className='form-group'>
                <label>
                  Organizations
                  <span className='org-id-disclaimer'>
                    *Do not share these codes with your employees
                  </span>
                </label>
                <div className='enrolled-orgs'>
                  {organizations.map((org) => (
                    <div key={org.org_id} className='enrolled-org-row'>
                      <span className='enrolled-org-name'>
                        {org.company_name}
                      </span>
                      <span className='enrolled-org-code'>
                        Code: {org.company_code}
                      </span>
                      {editMode && organizations.length > 1 && (
                        <button
                          type='button'
                          className='remove-org-btn'
                          onClick={() => handleRemoveOrg(org)}
                          title='Remove organization'
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className='form-group'>
                <label>Add Organization</label>
                <div className='add-org-row'>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Enter organization code'
                    value={newOrgCode}
                    onChange={(e) => {
                      setNewOrgCode(e.target.value);
                      setOrgError('');
                    }}
                    disabled={!editMode || addingOrg}
                  />
                  <button
                    type='button'
                    className='add-org-btn'
                    onClick={handleAddOrg}
                    disabled={!editMode || addingOrg || !newOrgCode.trim()}
                  >
                    {addingOrg ? 'Adding...' : 'Add'}
                  </button>
                </div>
                {orgError && <div className='validation-error'>{orgError}</div>}
              </div>
            </>
          )}

          <div className='form-group'>
            <label>Impairment Alerts</label>
            {validationErrors.notification_methods && (
              <div className='validation-error'>
                {validationErrors.notification_methods}
              </div>
            )}
            <div className='alerts-grid'>
              {organizations.length === 0 && (
                <div className='alerts-row alerts-row--empty'>
                  Add an organization above to configure alert preferences.
                </div>
              )}
              {organizations.map((org) => {
                const s = notificationsByOrg[org.org_id] || {};
                const isActive =
                  Boolean(s.phone_notification) ||
                  Boolean(s.email_notification);
                const orgError = orgSaveErrors[org.org_id];
                const rowClasses = [
                  'alerts-row',
                  isActive ? 'alerts-row--active' : 'alerts-row--inactive',
                  orgError ? 'alerts-row--errored' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <div key={org.org_id} className={rowClasses}>
                    <div className='alerts-row__org'>
                      <div className='alerts-row__org-name'>
                        {org.company_name}
                      </div>
                      <div className='alerts-row__org-code'>
                        Code {org.company_code}
                      </div>
                    </div>
                    <div className='alerts-row__toggles'>
                      <div className='alerts-row__toggle-group'>
                        <div className='checkbox-item'>
                          <input
                            type='checkbox'
                            id={`phone_notif_${org.org_id}`}
                            checked={Boolean(s.phone_notification)}
                            onChange={() =>
                              handleAlertToggle(
                                org.org_id,
                                'phone_notification',
                              )
                            }
                            disabled={!editMode}
                          />
                          <label htmlFor={`phone_notif_${org.org_id}`}>
                            Push notification
                          </label>
                        </div>
                        <div className='checkbox-item subsetting'>
                          <input
                            type='checkbox'
                            id={`push_only_${org.org_id}`}
                            checked={Boolean(s.push_only_impaired)}
                            onChange={() =>
                              handleAlertToggle(
                                org.org_id,
                                'push_only_impaired',
                              )
                            }
                            disabled={!editMode || !s.phone_notification}
                          />
                          <label htmlFor={`push_only_${org.org_id}`}>
                            Only when impaired
                          </label>
                        </div>
                      </div>
                      <div className='alerts-row__toggle-group'>
                        <div className='checkbox-item'>
                          <input
                            type='checkbox'
                            id={`email_notif_${org.org_id}`}
                            checked={Boolean(s.email_notification)}
                            onChange={() =>
                              handleAlertToggle(
                                org.org_id,
                                'email_notification',
                              )
                            }
                            disabled={!editMode}
                          />
                          <label htmlFor={`email_notif_${org.org_id}`}>
                            Email notification
                          </label>
                        </div>
                        <div className='checkbox-item subsetting'>
                          <input
                            type='checkbox'
                            id={`email_only_${org.org_id}`}
                            checked={Boolean(s.email_only_impaired)}
                            onChange={() =>
                              handleAlertToggle(
                                org.org_id,
                                'email_only_impaired',
                              )
                            }
                            disabled={!editMode || !s.email_notification}
                          />
                          <label htmlFor={`email_only_${org.org_id}`}>
                            Only when impaired
                          </label>
                        </div>
                      </div>
                    </div>
                    {orgError && (
                      <div className='alerts-row__error'>
                        <span>{orgError}</span>
                        <button
                          type='button'
                          className='alerts-row__error-retry'
                          onClick={() => retryOrgSave(org.org_id)}
                          disabled={saving}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className='form-actions'>
          {editMode && (
            <button
              type='button'
              className='cancel-button'
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
          )}
          <button type='submit' className='save-button' disabled={saving}>
            {!editMode
              ? 'Edit Settings'
              : saving
                ? 'Saving...'
                : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
